import Link from '@jetbrains/ring-ui/components/link/link'
import plur from 'plur'
import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {shallowEqual} from 'react-redux'

import {useAppDispatch, useAppSelector} from '../../../../../../../../../hooks/react-redux'
import {getEmptyHash} from '../../../../../../../../../utils/empty'
import {getDraft} from '../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {pipelineDraft} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import SettingsBlock from '../../../../SettingsBlock/SettingsBlock'

import PipelineParameter from './PipelineParameter/PipelineParameter'
import PipelineParameterForm from './PipelineParameterForm/PipelineParameterForm'
import {pipelineParametersNoticeLink} from './PipelineParameters.consts'

const COUNTER_LIMIT = 20

const PipelineParameters = () => {
  const [isCreateParameterMode, setCreateParameterMode] = useState<boolean>(false)
  const pipelineId = usePipelineId()
  const parameters: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, pipelineId)?.settings.parameters ?? getEmptyHash(),
  )
  const dispatch = useAppDispatch()
  const id = useId()
  const keys = useMemo(() => Object.keys(parameters), [parameters])
  const isOutOfLimit = keys.length >= COUNTER_LIMIT
  const count = keys.length
  const prevCount = useRef(count)
  const select = useCallback(
    (key: string) => {
      const input = document.getElementById(`${id}_${key}_name`) as HTMLInputElement | null
      input?.select()
    },
    [id],
  )
  useEffect(() => {
    if (count > prevCount.current) {
      select(keys[count - 1])
    }
    prevCount.current = count
  }, [count, keys, select])
  const addParameter = useCallback(() => setCreateParameterMode(true), [setCreateParameterMode])
  const deletedParameters = useAppSelector(
    state => state.pipelines.pipelineDraft[pipelineId]?.deleted?.parameters,
    shallowEqual,
  )
  const parametersCount = keys.filter(key => !deletedParameters?.includes(key)).length
  const settingsBlockTitle = parametersCount ? plur('Parameter', parametersCount) : 'No Parameters'
  const handlerApplySecretButtonClick = useCallback(
    (name: string, value: string) => {
      dispatch(
        pipelineDraft.actions.setParameter({
          id: pipelineId,
          name: `${name}`,
          value,
        }),
      )
      setCreateParameterMode(false)
    },
    [dispatch, pipelineId],
  )

  return useMemo(
    () => (
      <SettingsBlock
        id="parameters"
        collapsible={count > 0}
        title={settingsBlockTitle}
        details={
          <>
            <Link
              target="_blank"
              href={pipelineParametersNoticeLink}
              rel="noreferrer"
            >{`Name-value pairs`}</Link>
            {' that you can reference in build scripts using the %ParameterName% syntax'}
          </>
        }
        collapsedNote={Object.entries(parameters)
          .filter(([key]) => !deletedParameters?.includes(key))
          .map(([key, value]) => `${key}=${value}`)
          .join(', ')}
        onAddClick={!isOutOfLimit ? addParameter : undefined}
      >
        {keys.map(key => {
          const value = parameters[key]
          const isDeleted = deletedParameters?.includes(key)

          return <PipelineParameter key={key} value={value} name={key} deleted={isDeleted} />
        })}

        {isCreateParameterMode && (
          <PipelineParameterForm
            onConfirm={handlerApplySecretButtonClick}
            onCancel={() => setCreateParameterMode(false)}
          />
        )}
      </SettingsBlock>
    ),
    [
      count,
      settingsBlockTitle,
      parameters,
      isOutOfLimit,
      addParameter,
      keys,
      isCreateParameterMode,
      handlerApplySecretButtonClick,
      deletedParameters,
    ],
  )
}

export default PipelineParameters
